import { PAGE_LOAD_NONCE } from "@/lib/pageLoadNonce";
import { useCallback, useRef } from "react";
import { useTrack } from "./useTrack";

/**
 * Measures the time between iframe src being set and the `onLoad` event firing.
 * Fires a `dashboard_load` tracking event with the duration.
 */
export function useIframeLoadTime(dashboardId: string, slug: string, mode: "full" | "thumbnail") {
  const track = useTrack();
  const startRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);

  const onSrcSet = useCallback(() => {
    startRef.current = performance.now();
    // Wall-clock start is emitted, never reconstructed server-side as
    // `createdAt - loadMs`: deriving it from the duration would make concurrency
    // a function of slowness. Duration stays on the monotonic clock; the two
    // clocks are not interchangeable and must not be conflated.
    startedAtRef.current = Date.now();
  }, []);

  const onLoad = useCallback(() => {
    if (startRef.current === null || startedAtRef.current === null) return;
    const loadMs = Math.round(performance.now() - startRef.current);
    const startedAt = new Date(startedAtRef.current).toISOString();
    track("dashboard_load", {
      dashboardId,
      slug,
      mode,
      loadMs,
      nonce: PAGE_LOAD_NONCE,
      startedAt,
    });
    startRef.current = null;
    startedAtRef.current = null;
  }, [track, dashboardId, slug, mode]);

  return { onSrcSet, onLoad };
}
